// Libraries
import { useParams, generatePath } from "react-router-dom";
import { useState, useMemo, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown } from "@lockerpm/design";
import {
  Plus,
  Trash03,
  Star01,
  RefreshCcw05,
  DotsVertical,
  Tag01,
  UserDown01,
} from "@untitled-ui/icons-react";
import { DateTime } from "luxon";

// Resources
import { pathname } from "#src/config/pathname";

// General
import { useAppSelector } from "#src/store/hooks";
import constants from "#src/config/constants";

// Components
import { BreadcrumbV2 } from "#src/commonV2/Breadcrumb";
import { ButtonV2 } from "#src/commonV2/Button";
import { SearchboxV2 } from "#src/commonV2/Table/Searchbox";
import { useFilter } from "#src/commonV2/Table/Filter/useFilter";
import { TableV2 } from "#src/commonV2/Table";
import { TableActionLabelV2 } from "#src/commonV2/Table/TableActionLabel";
import { DropdownRenderV2 } from "#src/commonV2/antdHelpers/DropdownRender";
import { ObjectImage } from "#src/common/system/Object";

import { AddMembersModal } from "#src/components/workspace/member/AddMembersModal";
import { TransferOwnerModal } from "#src/components/workspace/member/TransferOwnerModal";
import { DeleteMemberModal } from "#src/components/workspace/member/DeleteMemberModal";

// API-related
import workspaceServices, {
  type IWorkspaceMember,
} from "#src/services/workspace";
import { WorkspaceMemberRoleValue } from "#src/config/filter/workspace/value";

// Children
import { memberRoleItems } from "#src/config/filter/workspace";
import useFetchPaginated from "#src/hooks/useFetchPaginated";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import { FilterDropdownV2 } from "#src/commonV2/Table/Filter/FilterDropdown";

const WorkspaceMembersPage = () => {
  const { t } = useTranslation("workspace", {
    keyPrefix: "member",
  });
  const { t: sidebarT } = useTranslation("layout", {
    keyPrefix: "sidebar.v2",
  });

  const { t: commonT } = useTranslation("common", {
    keyPrefix: "filter.searchBox",
  });

  const { workspaceId } = useParams<"workspaceId">();

  const currentWorkspace = useAppSelector(
    (state) => state.auth.currentWorkspace
  );

  const [openAddMembersModal, setOpenAddMembersModal] =
    useState<boolean>(false);
  const [openTransferOwnerModal, setOpenTransferOwnerModal] =
    useState<boolean>(false);
  const [openDeleteMemberModal, setOpenDeleteMemberModal] =
    useState<boolean>(false);

  // Pagination
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(constants.DEFAULT_PAGE_SIZE);

  // Search/sort
  const [searchKeyword, setSearchKeyword] = useState<string>("");

  // Selection
  const [selectedMemberIds, setSelectedMemberIds] = useState<number[]>([]);
  const [selectedMember, setSelectedMember] = useState<IWorkspaceMember | null>(
    null
  );

  const { value: memberRole, renderProps: roleFilterRenderProps } = useFilter({
    label: t("filter.role.label"),
    selectionList: memberRoleItems,
    options: {
      searchParamKey: "role",
      sideEffectOnChange: () => {
        setSelectedPage(1);
      },
    },
  });

  const fetchParams = useMemo(() => {
    if (workspaceId) {
      return [
        workspaceId,
        {
          roles: memberRole || undefined,
          q: searchKeyword,
          page: selectedPage,
          size: pageSize,
        },
      ] satisfies Parameters<typeof workspaceServices.list_members>;
    }
    return [];
  }, [workspaceId, memberRole, searchKeyword, selectedPage, pageSize]);

  const canAdd = useMemo(() => {
    return currentWorkspace?.role !== WorkspaceMemberRoleValue.MEMBER;
  }, [currentWorkspace?.role]);

  const canEdit = useMemo(() => {
    return currentWorkspace?.role === WorkspaceMemberRoleValue.OWNER;
  }, [currentWorkspace?.role]);

  const {
    list: memberList,
    count: memberCount,
    isLoading,
    updateData: updateMemberList,
  } = useFetchPaginated(workspaceServices.list_members, fetchParams);

  const disabledMemberStringIds = useMemo(() => {
    return memberList
      ?.filter((m) => m.role === WorkspaceMemberRoleValue.OWNER)
      .map((m) => m.id);
  }, [memberList]);

  const onChangePage = (page: number) => {
    setSelectedPage(page);
  };

  const onChangePageSize = (size: number) => {
    setPageSize(size);
    setSelectedPage(1);
  };

  const onPromoteToAdmin = (memberId: number) => {
    if (workspaceId) {
      workspaceServices
        .update_member(workspaceId, memberId, { role: "admin" })
        .then((response) => {
          if (response) {
            updateMemberList();
          }
        })
        .catch((error) => {
          apiErrorHandler(error, {
            toastMessage: t("notification.updateMember.fail"),
          });
        });
    }
  };

  const onDemoteToMember = (memberId: number) => {
    if (workspaceId) {
      workspaceServices
        .update_member(workspaceId, memberId, { role: "member" })
        .then((response) => {
          if (response.success) {
            updateMemberList();
          }
        })
        .catch((error) => {
          apiErrorHandler(error, {
            toastMessage: t("notification.updateMember.fail"),
          });
        });
    }
  };

  // Action list for kebab menu
  const getTableActions = (record: IWorkspaceMember) => {
    const actions: {
      key: string;
      label: ReactNode;
      action: () => void;
    }[] = [
      {
        key: "promote",
        label: (
          <TableActionLabelV2
            icon={<Star01 className="h-5 w-5" />}
            text={t("actions.promote")}
          />
        ),
        action: () => onPromoteToAdmin(record.id),
      },
      {
        key: "demote",
        label: (
          <TableActionLabelV2
            icon={<UserDown01 className="h-5 w-5" />}
            text={t("actions.demote")}
          />
        ),
        action: () => onDemoteToMember(record.id),
      },
      {
        key: "transfer",
        label: (
          <TableActionLabelV2
            icon={<RefreshCcw05 className="h-5 w-5" />}
            text={t("actions.transfer")}
          />
        ),
        action: () => {
          setSelectedMember(record);
          setOpenTransferOwnerModal(true);
        },
      },
      {
        key: "delete",
        label: (
          <TableActionLabelV2
            warning={true}
            icon={<Trash03 className="h-5 w-5" />}
            text={t("actions.delete")}
          />
        ),
        action: () => {
          setSelectedMember(record);
          setOpenDeleteMemberModal(true);
        },
      },
    ];
    if (record.role === WorkspaceMemberRoleValue.MEMBER) {
      return actions.filter((a) => !["demote"].includes(a.key));
    }
    if (record.role === WorkspaceMemberRoleValue.ADMIN) {
      return actions.filter((a) => !["promote"].includes(a.key));
    }
    return [];
  };

  return workspaceId ? (
    <>
      <div className="flex flex-col pt-5 pb-6 gap-5 overflow-x-hidden overflow-y-auto h-full">
        {/* Header (Breadcrumb + title) */}
        <div className="px-8 flex flex-col gap-4">
          <BreadcrumbV2
            levels={[
              {
                name: sidebarT("workspaceSettings.label"),
                path: generatePath(pathname.WORKSPACE_DETAILS, {
                  workspaceId,
                }),
              },
            ]}
            currentPage={{
              name: sidebarT("workspaceSettings.members"),
            }}
          />
          <div className="flex justify-between gap-4">
            <span className="text-display-xs font-semibold text-gray-v2-900">
              {t("title")}
            </span>
            {canAdd && (
              <ButtonV2
                size="md"
                onClick={() => {
                  setOpenAddMembersModal(true);
                }}
              >
                <Plus className="h-5 w-5 min-w-5" />
                {t("button.newMember")}
              </ButtonV2>
            )}
          </div>
        </div>
        {/* Header (Breadcrumb + title) end */}
        <div className="px-8 flex flex-col gap-4 flex-1">
          <div className="flex justify-between items-start gap-2">
            <div className="flex gap-4 justify-between flex-1">
              {/* Filters */}
              <div className="flex gap-2 flex-wrap">
                {selectedMemberIds.length === 0 ? (
                  <>
                    <FilterDropdownV2 {...roleFilterRenderProps} />
                  </>
                ) : null}
              </div>
              {/* Filters end */}

              {/* Bulk actions */}
              <div className="flex gap-2 flex-wrap justify-end">
                {selectedMemberIds.length > 0 ? (
                  <>
                    <ButtonV2
                      variant="secondary"
                      color="error"
                      onClick={() => {
                        setOpenDeleteMemberModal(true);
                      }}
                    >
                      <Trash03 className="h-5 w-5 min-w-5" />
                      {t("button.deleteMembers")}
                    </ButtonV2>
                  </>
                ) : null}
              </div>
              {/* Bulk actions end */}
            </div>
            {/* Searchbox */}
            <SearchboxV2
              value={searchKeyword}
              onChange={(value) => {
                setSearchKeyword(value);
              }}
              placeholder={commonT("placeholder")}
            />
            {/* Searchbox end */}
          </div>

          {/* Table */}
          <TableV2<IWorkspaceMember>
            data={memberList ?? []}
            getRowKey={(item) => item.id.toString()}
            isLoading={isLoading}
            columns={[
              {
                title: t("headers.number"),
                render: (_, index) => (
                  <span className="text-sm font-medium text-gray-v2-900">
                    {index < 9 ? `0${index + 1}` : index + 1}
                  </span>
                ),
                gridTemplateValue: "1fr",
              },
              {
                title: t("headers.member"),
                render: (item) => (
                  <div className="flex items-center gap-3">
                    <ObjectImage
                      data={item.user?.avatar}
                      className="h-10 w-10 min-w-[2rem] rounded-full border"
                    />
                    <p className="font-medium text-sm text-gray-v2-900 flex-1">
                      {item.user ? item.user.full_name : item.email}
                    </p>
                  </div>
                ),
                gridTemplateValue: "4fr",
                titleAlign: "left",
                overwriteWrapperProps: { className: "justify-start py-1.5" },
              },
              {
                title: t("headers.email"),
                render: (item) => (
                  <span className="text-sm font-regular text-gray-v2-600">
                    {item.user && item.user.email
                      ? item.user.email
                      : item.email}
                  </span>
                ),
                gridTemplateValue: "4fr",
              },
              {
                title: t("headers.roles"),
                render: (item) => {
                  const classType =
                    item.role === "owner"
                      ? "error"
                      : item.role === "admin"
                        ? "success"
                        : "blue";
                  const roleText =
                    item.role === "owner"
                      ? "roles.owner"
                      : item.role === "admin"
                        ? "roles.admin"
                        : "roles.member";
                  return (
                    <div
                      className={`bg-${classType}-v2-50 border border-${classType}-v2-200 text-${classType}-v2-700 rounded-full gap-1 flex py-0.5 pl-1 pr-2 items-center`}
                    >
                      <Tag01 className={`w-3 h-3 text-${classType}-v2-500`} />{" "}
                      <span className="text-xs font-medium">{t(roleText)}</span>
                    </div>
                  );
                },
                gridTemplateValue: "2fr",
              },
              {
                title: t("headers.accessDate"),
                render: (item) => (
                  <span className="text-sm font-regular text-gray-v2-600">
                    {DateTime.fromSeconds(item.access_time).toFormat(
                      "HH:mm:ss dd/MM/yyyy"
                    )}
                  </span>
                ),
                gridTemplateValue: "2fr",
              },
              {
                title: null,
                render: (item) => {
                  const actions = getTableActions(item);
                  const disabled =
                    actions.length === 0 ||
                    !canEdit ||
                    item.role === WorkspaceMemberRoleValue.OWNER;

                  return (
                    <Dropdown
                      menu={{
                        items: actions,
                        onClick: ({ key, domEvent }) => {
                          domEvent.preventDefault();
                          domEvent.stopPropagation();
                          actions
                            .find((action) => action.key === key)
                            ?.action();
                        },
                      }}
                      dropdownRender={(menu) => (
                        <DropdownRenderV2>{menu}</DropdownRenderV2>
                      )}
                      trigger={["click"]}
                      disabled={disabled}
                    >
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <DotsVertical
                          className={`h-5 w-5 ${disabled ? "text-gray-v2-200" : "text-gray-v2-400"} transition-colors duration-300`}
                        />
                      </button>
                    </Dropdown>
                  );
                },
                overwriteWrapperProps: { className: "justify-center" },
                gridTemplateValue: "1fr",
              },
            ]}
            rowSelection={
              canEdit
                ? {
                    selectedRowKeys: selectedMemberIds.map((item) =>
                      item.toString()
                    ),
                    disabledRowKeys: disabledMemberStringIds?.map((item) =>
                      item.toString()
                    ),
                    onChange: (value) => {
                      setSelectedMemberIds(
                        value
                          .map((item) => Number(item))
                          .filter(
                            (item) => !disabledMemberStringIds?.includes(item)
                          )
                      );
                    },
                  }
                : undefined
            }
            pagination={{
              count: memberCount,
              selected: selectedPage,
              onChange: onChangePage,
              size: { value: pageSize, onChange: onChangePageSize },
            }}
            options={{
              fitPageHeight: true,
            }}
          />
          {/* Table end */}
        </div>
      </div>
      <AddMembersModal
        workspaceId={workspaceId}
        open={openAddMembersModal}
        currentWorkspace={currentWorkspace}
        onClose={() => setOpenAddMembersModal(false)}
        updateMemberList={() => updateMemberList()}
      />
      <TransferOwnerModal
        workspaceId={workspaceId}
        open={openTransferOwnerModal}
        selectedMember={selectedMember}
        onClose={() => {
          setSelectedMember(null);
          setOpenTransferOwnerModal(false);
        }}
        updateMemberList={() => updateMemberList()}
      />
      <DeleteMemberModal
        workspaceId={workspaceId}
        open={openDeleteMemberModal}
        selectedMemberIds={
          selectedMember ? [selectedMember.id] : selectedMemberIds
        }
        memberList={memberList || []}
        setSelectedMemberIds={setSelectedMemberIds}
        onClose={() => {
          setSelectedMember(null);
          setOpenDeleteMemberModal(false);
        }}
        updateMemberList={() => updateMemberList()}
      />
    </>
  ) : null;
};

export default WorkspaceMembersPage;
